import type { NavChild, NavItem } from "@repo/shared-types/approvals";
import type { HelpdeskUnreadCount } from "@repo/shared-types/helpdesk";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { helpdeskRequesterApi } from "../../helpdesk/web/requester-api.ts";
import { AppFooter } from "./AppFooter.tsx";
import { useAppPathname, useTenantSlug } from "./app-pathname.ts";
import { handleSessionExpiry, markAuthenticated } from "./auth-redirect.ts";
import { me, RequestError } from "./client.ts";
import { NavCount, titleCount, useUnreadRequestCount } from "./nav-counts.tsx";
import { RouteContent } from "./RouteContent.tsx";
import { signOutAndLeave } from "./sign-out.ts";
import { TenantGate } from "./TenantGate.tsx";
import { UnsavedChanges, useUnsavedChangesNavigation } from "./UnsavedChanges.tsx";
import { buttonClass } from "./ui/index.ts";

// Detect expiry on idle pages; this check never extends the one-hour session.
const REFRESH_INTERVAL_MS = 5 * 60_000;

const redirectToLogin = (url: string): void => {
  window.location.href = url;
};

// A 401 here is an early expiry signal, so it bounces to sign-in rather than
// leaving a dead page until some later query happens to fire. Any other failure
// is ignored until the next check.
const pingSessionRefresh = (): void => {
  void fetch("/auth/refresh", { method: "POST", credentials: "include" })
    .then((res) => {
      if (res.status === 401) handleSessionExpiry(new RequestError(401, null), window.location, redirectToLogin);
    })
    .catch(() => undefined);
};

// Extending the session is a plain timer. The visibility check is what makes an
// abandoned tab lapse on schedule: browsers throttle background timers but do
// not reliably stop them, so leaving it to the browser would renew forever.
const useSessionRefresh = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") pingSessionRefresh();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled]);
};

// A previously-authenticated session that expires surfaces as a 401 on the next
// query, mutation, or existing poll; bounce to sign-in (preserving the URL)
// rather than leaving a dead page whose actions all fail.
const onSessionError = (error: unknown): void => {
  handleSessionExpiry(error, window.location, redirectToLogin);
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onSuccess: (_data, query) => {
      if (query.queryKey[0] !== "me") return;
      markAuthenticated();
    },
    onError: onSessionError,
  }),
  mutationCache: new MutationCache({ onError: onSessionError }),
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
  },
});

const REQUESTS_NAV_ID = "helpdesk-requests";

const UnreadCountContext = createContext<HelpdeskUnreadCount | null>(null);

const NavLabel = ({ item }: { item: NavChild | NavItem }) => {
  const count = useContext(UnreadCountContext);
  return (
    <>
      {item.label}
      {item.id === REQUESTS_NAV_ID && <NavCount count={count} />}
    </>
  );
};

const ParentLabel = ({ item, active }: { item: NavItem; active: boolean }) => {
  const count = useContext(UnreadCountContext);
  return (
    <>
      {item.label}
      {!active && item.children?.some((child) => child.id === REQUESTS_NAV_ID) && <NavCount count={count} />}
    </>
  );
};

const hasRequestsNav = (nav: readonly NavItem[]): boolean => nav.some((item) => item.id === REQUESTS_NAV_ID || item.children?.some((child) => child.id === REQUESTS_NAV_ID));

const matchesPath = (pathname: string, href: string): boolean => pathname === href || pathname.startsWith(`${href}/`);

const activePath = (entry: NavChild): string => entry.activePath ?? entry.href;

const activeChildId = (item: NavItem, pathname: string): string | null => {
  const matches = item.children?.filter((child) => matchesPath(pathname, activePath(child))).sort((left, right) => activePath(right).length - activePath(left).length) ?? [];
  return matches[0]?.id ?? null;
};

const HeaderNavItem = ({ item, pathname, onNavigate }: { item: NavItem; pathname: string; onNavigate: () => void }) => {
  if (!item.children) {
    const active = matchesPath(pathname, activePath(item));
    return (
      <div className="nav-item">
        <Link to={item.href} className="nav-link" data-status={active ? "active" : undefined} aria-current={active ? "page" : undefined} onClick={onNavigate}>
          <NavLabel item={item} />
        </Link>
      </div>
    );
  }

  const currentChildId = activeChildId(item, pathname);
  // The trigger is active on any section route, including the section root (e.g. the
  // /admin hub) where no individual child matches.
  const sectionActive = currentChildId !== null || matchesPath(pathname, activePath(item));
  return (
    <div className="nav-item nav-menu">
      <Link to={item.href} className="nav-link nav-menu__trigger" data-status={sectionActive ? "active" : undefined} onClick={onNavigate}>
        <ParentLabel item={item} active={sectionActive} />
      </Link>
      {/* activeOptions exact: true stops the router prefix-marking a section-root child (e.g. /helpdesk) active on every
          child route, which would highlight two items; activeChildId stays the source of truth. Same in MobileNavItem —
          see docs/user-and-navigation.md. */}
      <div className="nav-menu__items">
        {item.children.map((child) => {
          const active = child.id === currentChildId;
          return (
            <Link
              key={child.id}
              to={child.href}
              activeOptions={{ exact: true }}
              className="nav-link nav-menu__item"
              data-status={active ? "active" : undefined}
              aria-current={active ? "page" : undefined}
              onClick={onNavigate}
            >
              {child.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const CategorySubnav = ({ nav, pathname, onNavigate }: { nav: NavItem[]; pathname: string; onNavigate: () => void }) => {
  const section = nav.find((item) => item.children && (matchesPath(pathname, activePath(item)) || item.children.some((child) => matchesPath(pathname, activePath(child)))));
  if (!section?.children) return null;
  const currentChildId = activeChildId(section, pathname);

  return (
    <nav className="category-subnav" aria-label={`${section.label} sections`}>
      {/* The root keeps the router's default prefix matching, so it stays highlighted across the whole
          section. Rendering data-status/aria-current here would be inert: the router spreads its own
          after user props whenever the link matches — see docs/user-and-navigation.md. */}
      <Link to={section.href} className="category-subnav__root" onClick={onNavigate}>
        {section.label}
      </Link>
      <div className="category-subnav__links">
        {section.children.map((child) => {
          const active = child.id === currentChildId;
          return (
            <Link
              key={child.id}
              to={child.href}
              activeOptions={{ exact: true }}
              className="category-subnav__link"
              data-status={active ? "active" : undefined}
              aria-current={active ? "page" : undefined}
              onClick={onNavigate}
            >
              <NavLabel item={child} />
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

const MobileNavItem = ({ item, pathname, onNavigate }: { item: NavItem; pathname: string; onNavigate: () => void }) => {
  if (!item.children) {
    const active = matchesPath(pathname, activePath(item));
    return (
      <Link to={item.href} className="mobile-nav__link" data-status={active ? "active" : undefined} aria-current={active ? "page" : undefined} onClick={onNavigate}>
        <NavLabel item={item} />
      </Link>
    );
  }

  const currentChildId = activeChildId(item, pathname);
  const sectionActive = currentChildId !== null || matchesPath(pathname, activePath(item));
  return (
    <section className="mobile-nav__section" aria-labelledby={`mobile-nav-${item.id}`}>
      <div id={`mobile-nav-${item.id}`} className="mobile-nav__heading" data-status={sectionActive ? "active" : undefined}>
        <ParentLabel item={item} active={sectionActive} />
      </div>
      <div className="mobile-nav__children">
        {item.children.map((child) => {
          const active = child.id === currentChildId;
          return (
            <Link
              key={child.id}
              to={child.href}
              activeOptions={{ exact: true }}
              className="mobile-nav__link"
              data-status={active ? "active" : undefined}
              aria-current={active ? "page" : undefined}
              onClick={onNavigate}
            >
              {child.label}
            </Link>
          );
        })}
      </div>
    </section>
  );
};

const Header = () => {
  const meQuery = useQuery({ queryKey: ["me"], queryFn: me });
  const pathname = useAppPathname();
  const slug = useTenantSlug();
  const { confirmLeave, allowUnload } = useUnsavedChangesNavigation();
  const signOut = (): void => {
    if (confirmLeave()) void signOutAndLeave(slug, allowUnload);
  };
  const headerRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeNavigation = (): void => {
    setMobileOpen(false);
  };

  useEffect(() => closeNavigation(), [pathname]);

  useEffect(() => {
    const closeOnOutsideClick = (event: PointerEvent): void => {
      if (headerRef.current && event.target instanceof Node && !headerRef.current.contains(event.target)) closeNavigation();
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || !mobileOpen) return;
      closeNavigation();
      menuButtonRef.current?.focus();
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileOpen]);

  if (meQuery.isLoading) return null;

  if (meQuery.isError || !meQuery.data) {
    return (
      <div className="app-shell-header">
        <header className="app-header">
          <span className="app-header__brand">Linear Portal</span>
        </header>
      </div>
    );
  }

  return (
    <div className="app-shell-header" ref={headerRef}>
      <header className="app-header">
        <Link to="/" className="app-header__brand" onClick={closeNavigation}>
          Linear Portal
        </Link>
        <nav className="app-header__nav" aria-label="Portal">
          {meQuery.data.nav.map((item) => (
            <HeaderNavItem key={item.id} item={item} pathname={pathname} onNavigate={closeNavigation} />
          ))}
        </nav>
        <div className="app-header__account">
          <Link to="/profile" className="nav-link">
            {meQuery.data.name}
          </Link>
          <button type="button" onClick={signOut} className={buttonClass("ghost")}>
            Sign out
          </button>
        </div>
        <button
          ref={menuButtonRef}
          type="button"
          className={`${buttonClass("secondary")} app-header__menu-button`}
          aria-expanded={mobileOpen}
          aria-controls="mobile-navigation"
          onClick={() => setMobileOpen((open) => !open)}
        >
          Menu
        </button>
        <nav id="mobile-navigation" className="mobile-nav" aria-label="Portal menu" hidden={!mobileOpen}>
          {meQuery.data.nav.map((item) => (
            <MobileNavItem key={item.id} item={item} pathname={pathname} onNavigate={closeNavigation} />
          ))}
          <div className="mobile-nav__account">
            <Link to="/profile" className="mobile-nav__link" onClick={closeNavigation}>
              {meQuery.data.name}
            </Link>
            <button type="button" onClick={signOut} className={buttonClass("ghost")}>
              Sign out
            </button>
          </div>
        </nav>
      </header>
      <CategorySubnav nav={meQuery.data.nav} pathname={pathname} onNavigate={closeNavigation} />
    </div>
  );
};

const PUBLIC_PATHS: readonly string[] = ["/approvals/email-action"];

// Reads the already-fetched identity: the header owns the fetch, and this must not run before the tenant gate has admitted the visitor.
const useRequestsUnreadCount = (publicPage: boolean): HelpdeskUnreadCount | null => {
  const identity = useQuery({ queryKey: ["me"], queryFn: me, enabled: false });
  const enabled = !publicPage && identity.data !== undefined && hasRequestsNav(identity.data.nav);
  return useUnreadRequestCount(helpdeskRequesterApi.unreadCount, ["helpdesk", "requests", identity.data?.email], enabled);
};

const Shell = () => {
  const pathname = useAppPathname();
  const publicPage = PUBLIC_PATHS.includes(pathname);
  const unread = useRequestsUnreadCount(publicPage);
  useSessionRefresh(!publicPage);
  return (
    <UnreadCountContext.Provider value={unread}>
      <div className="app-main">
        <a className="skip-link" href="#main-content">
          Skip to content
        </a>
        {publicPage ? (
          <RouteContent pathname={pathname}>
            <Outlet />
          </RouteContent>
        ) : (
          <TenantGate header={<Header />} footer={<AppFooter />} titleCount={titleCount(unread)}>
            <Outlet />
          </TenantGate>
        )}
      </div>
    </UnreadCountContext.Provider>
  );
};

export const RootLayout = () => (
  <QueryClientProvider client={queryClient}>
    <UnsavedChanges>
      <Shell />
    </UnsavedChanges>
  </QueryClientProvider>
);
